<script lang="ts" setup>
import {
  onMounted, onUnmounted, ref, computed, watch,
} from 'vue';
import { auth } from '@/config/firebase';
import WorkspaceService from '@/service/workspace.service';
import type {
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceStatus,
  WorkspacePriority,
  NewWorkspaceItem,
} from '@/types/workspace';
import NoteEditor from '@/components/NoteEditor.vue';
import UserService from '@/service/user.service';
import KPIsDashboard from '@/views/AdminPanel/KPIsDashboard.vue';

type Unsubscribe = () => void;
type FireDate = Date | { seconds: number };
type TeamMember = {
  id: string;
  fullName?: string;
  email?: string;
  userId?: string;
  roles?: string[];
};

const allItems = ref<WorkspaceItem[]>([]);
const tab = ref<'task' | 'idea' | 'kpi'>('task');
const unsubscribe = ref<Unsubscribe | null>(null);

// Notes UX state
const ideaSearch = ref('');
const selectedIdeaId = ref<string | null>(null);
const ideaEditor = ref('');
const ideaTitle = ref('');
const ideaLabels = ref<string>('');
const ideaPinned = ref(false);
const ideaColor = ref('#0C0C0C');

// legacy quick-add model (unused, replaced by modal) kept for potential future use
// const taskTitle = ref('');
// const taskDescription = ref('');
// const taskPriority = ref<WorkspacePriority>('Medium');
// const taskLabels = ref<string>('');
// const taskDueDate = ref<string>('');

// Team members for assignment
const teamMembers = ref<TeamMember[]>([]);
const assignOpenFor = ref<string | null>(null);
const assigneesSelection = ref<Record<string, Set<string>>>({});

const statusesByType: Record<WorkspaceItemType, WorkspaceStatus[]> = {
  idea: ['New', 'Prioritized', 'In Progress', 'Launched'],
  task: ['Backlog', 'In Progress', 'Done'],
};

const statuses = computed(() => {
  if (tab.value === 'task' || tab.value === 'idea') {
    return statusesByType[tab.value as 'task' | 'idea'];
  }
  return [] as WorkspaceStatus[];
});

const itemsByStatus = computed<Record<string, WorkspaceItem[]>>(() => {
  const grouped: Record<string, WorkspaceItem[]> = {};
  statuses.value.forEach((s) => {
    grouped[s] = [];
  });
  allItems.value
    .filter((x) => x.type === tab.value)
    .forEach((item) => {
      if (!grouped[item.status]) grouped[item.status] = [];
      grouped[item.status].push(item);
    });
  return grouped;
});

const ideaItems = computed(() => allItems.value.filter((x) => x.type === 'idea'));
const filteredIdeas = computed(() => {
  const q = ideaSearch.value.trim().toLowerCase();
  const items = [...ideaItems.value].sort(
    (a, b) => Number(b.pinned) - Number(a.pinned),
  );
  if (!q) return items;
  return items.filter((i) => {
    const haystack = [
      i.title || '',
      i.description || '',
      (i.labels || []).join(','),
      i.contentHtml || '',
    ].join(' ');
    return haystack.toLowerCase().includes(q);
  });
});

const selectedIdea = computed(
  () => ideaItems.value.find((i) => i.id === selectedIdeaId.value) || null,
);

watch(selectedIdea, (val) => {
  ideaEditor.value = val?.contentHtml || '';
  ideaTitle.value = val?.title || '';
  ideaLabels.value = (val?.labels || []).join(', ');
  ideaPinned.value = !!val?.pinned;
  ideaColor.value = val?.color || '#0C0C0C';
});

function stopSubscription() {
  if (unsubscribe.value) {
    unsubscribe.value();
    unsubscribe.value = null;
  }
}

function startSubscription() {
  stopSubscription();
  unsubscribe.value = WorkspaceService.subscribeItems((items) => {
    allItems.value = items;
  });
}

onMounted(() => {
  startSubscription();
});
onUnmounted(() => {
  stopSubscription();
});
// Fetch team
onMounted(async () => {
  try {
    teamMembers.value = await UserService.getTeamMembers();
  } catch (e) {
    // noop
  }
});
function parseLabels(input: string): string[] | undefined {
  const parts = input
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return parts.length ? parts : undefined;
}

async function addIdea() {
  const payload: NewWorkspaceItem = {
    type: 'idea',
    title: (ideaTitle.value || 'Untitled').trim(),
    contentHtml: ideaEditor.value,
    status: 'New',
    labels: parseLabels(ideaLabels.value),
    createdBy: auth.currentUser?.email || 'unknown',
    pinned: ideaPinned.value,
    color: ideaColor.value,
  };
  const id = await WorkspaceService.addItem(payload);
  selectedIdeaId.value = id;
}

async function saveCurrentIdea() {
  if (!selectedIdea.value) return;
  await WorkspaceService.updateItem(selectedIdea.value.id, {
    title: (ideaTitle.value || 'Untitled').trim(),
    contentHtml: ideaEditor.value,
    labels: parseLabels(ideaLabels.value),
    pinned: ideaPinned.value,
    color: ideaColor.value,
  });
}

async function togglePin(idea: WorkspaceItem) {
  await WorkspaceService.updateItem(idea.id, { pinned: !idea.pinned });
}

async function duplicateIdea(idea: WorkspaceItem) {
  const copy: NewWorkspaceItem = {
    type: 'idea',
    title: `${idea.title} (copy)`,
    contentHtml: idea.contentHtml,
    description: idea.description,
    status: idea.status,
    labels: idea.labels,
    createdBy: auth.currentUser?.email || 'unknown',
    pinned: false,
    color: idea.color,
  };
  const id = await WorkspaceService.addItem(copy);
  selectedIdeaId.value = id;
}

// Legacy quick-add kept for reference; modal flow replaces it
// async function addTask() {
//   const payload: NewWorkspaceItem = {
//     type: 'task',
//     title: taskTitle.value.trim(),
//     description: taskDescription.value.trim(),
//     status: 'Backlog',
//     priority: taskPriority.value,
//     labels: parseLabels(taskLabels.value),
//     dueDate: taskDueDate.value ? (new Date(taskDueDate.value) as Date) : undefined,
//     createdBy: auth.currentUser?.email || 'unknown',
//   };
//   if (!payload.title) return;
//   await WorkspaceService.addItem(payload);
//   taskTitle.value = '';
//   taskDescription.value = '';
//   taskLabels.value = '';
//   taskDueDate.value = '';
// }

// manual move removed in favor of drag and drop

// Drag & Drop for tasks
const dragItemId = ref<string | null>(null);
const dragOverStatus = ref<WorkspaceStatus | null>(null);

function onDragStart(item: WorkspaceItem) {
  dragItemId.value = item.id;
}

function onDragOver(status: WorkspaceStatus) {
  dragOverStatus.value = status;
}

async function onDrop(status: WorkspaceStatus) {
  const currentDraggedId = dragItemId.value;
  dragOverStatus.value = null;
  dragItemId.value = null;
  if (!currentDraggedId) return;
  const item = allItems.value.find((x) => x.id === currentDraggedId);
  if (!item || item.type !== 'task' || item.status === status) return;
  await WorkspaceService.updateItem(item.id, { status });
}

function initialsOf(member: TeamMember): string {
  const src = (member.fullName && member.fullName.trim()) || member.email || '';
  if (!src) return '—';
  const parts = src.split(/\s|\.|@/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const second = parts.length > 1 ? parts[1]?.[0] : '';
  return (first + second).toUpperCase();
}

function initialsOfAssignee(a: { fullName?: string; email?: string }): string {
  const src = (a.fullName && a.fullName.trim()) || a.email || '';
  if (!src) return '—';
  const parts = src.split(/\s|\.|@/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const second = parts.length > 1 ? parts[1]?.[0] : '';
  return (first + second).toUpperCase();
}

function openAssign(item: WorkspaceItem) {
  assignOpenFor.value = item.id;
  const key = item.id;
  const emails = (item.assignees || [])
    .map((a: { email?: string }) => a.email)
    .filter(Boolean) as string[];
  assigneesSelection.value[key] = new Set<string>(emails);
}

function toggleAssignee(itemId: string, email: string) {
  const set = assigneesSelection.value[itemId] || new Set<string>();
  if (set.has(email)) set.delete(email);
  else set.add(email);
  assigneesSelection.value[itemId] = set;
}

async function saveAssignees(item: WorkspaceItem) {
  const key = item.id;
  const emails = Array.from(assigneesSelection.value[key] || new Set<string>());
  const selected = teamMembers.value
    .filter((m) => emails.includes(m.email || ''))
    .map((m) => ({
      userId: m.userId || m.id,
      fullName: m.fullName,
      email: m.email,
    }));
  await WorkspaceService.updateItem(item.id, { assignees: selected });
  assignOpenFor.value = null;
}

function cancelAssign() {
  assignOpenFor.value = null;
}

const commentsOpenFor = ref<string | null>(null);
const commentsLoading = ref(false);
const comments = ref<
  { id: string; author?: string; message: string; createdAt?: FireDate }[]
>([]);
const newComment = ref('');

// Ticket detail modal state
const showTicketModal = ref(false);
const activeTicket = ref<WorkspaceItem | null>(null);
const activeTicketComments = ref<
  { id: string; author?: string; message: string; createdAt?: FireDate }[]
>([]);
const activeTicketCommentsLoading = ref(false);
const newTicketComment = ref('');

async function toggleComments(itemId: string) {
  if (commentsOpenFor.value === itemId) {
    commentsOpenFor.value = null;
    comments.value = [];
    return;
  }
  commentsOpenFor.value = itemId;
  commentsLoading.value = true;
  comments.value = await WorkspaceService.getComments(itemId);
  commentsLoading.value = false;
}

async function submitComment(itemId: string) {
  const message = newComment.value.trim();
  if (!message) return;
  await WorkspaceService.addComment(itemId, {
    author: auth.currentUser?.email || 'unknown',
    message,
  });
  newComment.value = '';
  // reload
  comments.value = await WorkspaceService.getComments(itemId);
}

// delete handled elsewhere (removed to satisfy linter)

function formatDate(date: FireDate | undefined) {
  try {
    if (!date) return '—';
    const asAny = date as unknown as { seconds?: number };
    const d = asAny && typeof asAny.seconds === 'number'
      ? new Date(asAny.seconds * 1000)
      : new Date(date as Date);
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
}

// Ticket detail modal
async function openTicket(item: WorkspaceItem) {
  if (item.type !== 'task') return;
  activeTicket.value = item;
  showTicketModal.value = true;
  activeTicketCommentsLoading.value = true;
  activeTicketComments.value = await WorkspaceService.getComments(item.id);
  activeTicketCommentsLoading.value = false;
}

function closeTicket() {
  showTicketModal.value = false;
  activeTicket.value = null;
  activeTicketComments.value = [];
  newTicketComment.value = '';
}

async function submitTicketComment() {
  if (!activeTicket.value) return;
  const message = newTicketComment.value.trim();
  if (!message) return;
  await WorkspaceService.addComment(activeTicket.value.id, {
    author: auth.currentUser?.email || 'unknown',
    message,
  });
  newTicketComment.value = '';
  activeTicketComments.value = await WorkspaceService.getComments(
    activeTicket.value.id,
  );
}

// New Task Modal (liquid glass)
const showAddTaskModal = ref(false);
const newTaskTitle = ref('');
const newTaskDescription = ref('');
const newTaskPriority = ref<WorkspacePriority>('Medium');
const newTaskDueDate = ref<string>('');
const newTaskLabels = ref<string>('');
const newTaskError = ref<string>('');
const isCreatingTask = ref(false);

function openNewTaskModal() {
  newTaskTitle.value = '';
  newTaskDescription.value = '';
  newTaskPriority.value = 'Medium';
  newTaskDueDate.value = '';
  newTaskLabels.value = '';
  newTaskError.value = '';
  showAddTaskModal.value = true;
}

function closeNewTaskModal() {
  showAddTaskModal.value = false;
}

async function submitNewTask() {
  newTaskError.value = '';
  const title = newTaskTitle.value.trim();
  if (!title) {
    newTaskError.value = 'Please enter a clear, actionable task title.';
    return;
  }
  isCreatingTask.value = true;
  try {
    const payload: NewWorkspaceItem = {
      type: 'task',
      title,
      description: newTaskDescription.value.trim(),
      status: 'Backlog',
      priority: newTaskPriority.value,
      labels: parseLabels(newTaskLabels.value),
      dueDate: newTaskDueDate.value ? (new Date(newTaskDueDate.value) as Date) : undefined,
      createdBy: auth.currentUser?.email || 'unknown',
    };
    await WorkspaceService.addItem(payload);
    showAddTaskModal.value = false;
  } catch (e: unknown) {
    let msg = 'Something went wrong while creating the task.';
    if (typeof e === 'object' && e !== null) {
      const anyErr = e as { message?: string; code?: string };
      msg = anyErr.message || anyErr.code || msg;
    }
    newTaskError.value = `Could not create task. ${msg}`;
  } finally {
    isCreatingTask.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen bg-black-primary text-white p-6">
    <div class="max-w-7xl mx-auto">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-gold-primary">
            TALAB Workspace
          </h1>
          <p class="text-gray-300">
            Ideas, planning and delivery in one place.
          </p>
        </div>
        <img
          src="@/assets/gold-logo-transparent.png"
          alt="TALAB"
          class="w-20"
        />
      </div>

      <div class="flex gap-2 mb-6">
        <button class="px-4 py-2 rounded-lg border" :class="tab === 'task' ? 'bg-gold-primary text-black border-gold-secondary' : 'bg-black-secondary text-gold-primary border-zinc-700'" @click="tab = 'task'">Tasks</button>
        <button class="px-4 py-2 rounded-lg border" :class="tab === 'idea' ? 'bg-gold-primary text-black border-gold-secondary' : 'bg-black-secondary text-gold-primary border-zinc-700'" @click="tab = 'idea'">Ideas</button>
        <button class="px-4 py-2 rounded-lg border" :class="tab === 'kpi' ? 'bg-gold-primary text-black border-gold-secondary' : 'bg-black-secondary text-gold-primary border-zinc-700'" @click="tab = 'kpi'">KPIs</button>
      </div>

      <!-- Ideas Split Pane -->
      <div v-if="tab === 'idea'" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <!-- Left: list -->
        <div
          class="lg:col-span-1 bg-black-secondary rounded-2xl border border-zinc-800"
        >
          <div class="p-3 border-b border-zinc-800">
            <input
              v-model="ideaSearch"
              placeholder="Search ideas…"
              class="w-full px-3 py-2 rounded bg-black-primary border border-zinc-700 placeholder:text-zinc-500"
            />
          </div>
          <div class="p-2 max-h-[70vh] overflow-auto">
            <button
              @click="
                selectedIdeaId = null;
                ideaEditor = '';
                ideaTitle = '';
                ideaLabels = '';
                ideaPinned = false;
                ideaColor = '#0C0C0C';
              "
              class="w-full mb-2 px-3 py-2 rounded bg-gold-primary text-black hover:bg-gold-secondary"
            >
              + New note
            </button>
            <div
              v-for="idea in filteredIdeas"
              :key="idea.id"
              @click="selectedIdeaId = idea.id"
              class="p-3 rounded-xl border mb-2 cursor-pointer"
              :class="
                selectedIdeaId === idea.id
                  ? 'border-gold-primary bg-black-primary'
                  : 'border-zinc-800 bg-black-primary/60'
              "
              :style="{ borderColor: idea.pinned ? '#BC8809' : undefined }"
            >
              <div class="flex items-center justify-between">
                <div class="font-semibold truncate">
                  {{ idea.title || "Untitled" }}
                </div>
                <button
                  @click.stop="togglePin(idea)"
                  class="text-xs px-2 py-0.5 rounded border"
                  :class="
                    idea.pinned
                      ? 'border-gold-primary text-gold-primary'
                      : 'border-zinc-700 text-zinc-400'
                  "
                >
                  Pin
                </button>
              </div>
              <div
                class="text-xs text-gray-400 mt-1 line-clamp-2"
                v-html="idea.contentHtml"
              ></div>
              <div class="flex flex-wrap gap-1 mt-2">
                <span
                  v-for="lbl in idea.labels || []"
                  :key="lbl"
                  class="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-300"
                  >{{ lbl }}</span
                >
              </div>
            </div>
          </div>
        </div>
        <!-- Right: editor -->
        <div
          class="lg:col-span-2 bg-black-secondary rounded-2xl border border-zinc-800 p-4"
        >
          <div class="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
            <input
              v-model="ideaTitle"
              placeholder="Title — capture the essence"
              class="md:col-span-3 px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500"
            />
            <input
              v-model="ideaLabels"
              placeholder="Labels — growth, product, ops"
              class="md:col-span-2 px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500"
            />
            <div class="flex items-center gap-2">
              <label class="text-xs text-gray-400">Color</label>
              <input
                v-model="ideaColor"
                type="color"
                class="w-10 h-10 rounded border border-zinc-700 bg-black-primary p-0"
              />
              <label class="text-xs text-gray-400 ml-2">Pinned</label>
              <input v-model="ideaPinned" type="checkbox" class="w-4 h-4" />
            </div>
          </div>
          <NoteEditor
            v-model="ideaEditor"
            :placeholder="'H1, H2, text and bold — keep it sharp'"
          />
          <div class="mt-3 flex items-center justify-end gap-2">
            <button
              @click="addIdea"
              class="px-4 py-2 rounded-lg bg-gold-primary text-black font-semibold hover:bg-gold-secondary"
            >
              Save as new
            </button>
            <button
              :disabled="!selectedIdea"
              @click="saveCurrentIdea"
              class="px-4 py-2 rounded-lg bg-black-primary text-gold-primary border border-gold-primary disabled:opacity-50"
            >
              Save changes
            </button>
            <button
              v-if="selectedIdea"
              @click="duplicateIdea(selectedIdea)"
              class="px-4 py-2 rounded-lg bg-black-primary text-zinc-200 border border-zinc-700"
            >
              Duplicate
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="tab === 'task'" class="bg-black-secondary rounded-2xl p-4 border border-zinc-800 mb-6">
        <!-- <div class="grid grid-cols-1 md:grid-cols-7 gap-3">
          <input
            v-model="taskTitle"
            placeholder="Task — write a clear, actionable title"
            class="md:col-span-2 px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500"
          />
          <input
            v-model="taskDescription"
            placeholder="Details — acceptance criteria, links"
            class="md:col-span-3 px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500"
          />
          <select
            v-model="taskPriority"
            class="px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 text-zinc-200"
          >
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
            <option>Urgent</option>
          </select>
          <input
            v-model="taskDueDate"
            type="date"
            class="px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 text-zinc-200"
          />
          <input
            v-model="taskLabels"
            placeholder="Labels — performance, seo, ops"
            class="px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500"
          />
        </div> -->
        <div class="mt-3 flex justify-end">
          <button
            @click="openNewTaskModal"
            class="px-5 py-2.5 rounded-lg bg-gold-primary text-black font-semibold hover:bg-gold-secondary"
          >
            Add Task
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div
            v-for="s in statuses"
            :key="s"
            class="bg-black-secondary rounded-2xl p-3 border border-zinc-800"
            @dragover.prevent="onDragOver(s)"
            @drop.prevent="onDrop(s)"
            :class="dragOverStatus === s ? 'ring-1 ring-gold-primary' : ''"
          >
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-lg font-semibold text-gold-primary">{{ s }}</h3>
              <span class="text-xs text-gray-400">{{
                itemsByStatus[s]?.length || 0
              }}</span>
            </div>
            <div class="space-y-3 min-h-[80px]">
              <div
                v-for="item in itemsByStatus[s]"
                :key="item.id"
                class="bg-black-primary/70 rounded-xl border border-zinc-700 p-3 cursor-pointer"
                draggable="true"
                @dragstart="onDragStart(item)"
                @click="openTicket(item)"
              >
                <div
                  class="flex -space-x-1 mb-2"
                  v-if="item.assignees && item.assignees.length"
                  @click.stop
                >
                  <div
                    v-for="a in item.assignees"
                    :key="a.userId || a.email"
                    class="inline-flex items-center justify-center rounded-full border border-zinc-700 bg-black-secondary text-[10px] h-6 w-6 text-gold-primary"
                  >
                    {{
                      initialsOfAssignee({
                        fullName: a.fullName,
                        email: a.email,
                      })
                    }}
                  </div>
                </div>
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="font-semibold">{{ item.title }}</div>
                    <div
                      v-if="item.description"
                      class="text-sm text-gray-400 line-clamp-3"
                    >
                      {{ item.description }}
                    </div>
                    <div class="flex flex-wrap gap-1 mt-2">
                      <span
                        v-for="lbl in item.labels || []"
                        :key="lbl"
                        class="text-[10px] px-2 py-0.5 rounded-full border border-gold-primary text-gold-primary"
                        >{{ lbl }}</span
                      >
                    </div>
                  </div>
                  <div class="text-right" />
                </div>
                <div
                  class="mt-2 text-xs text-gray-500 flex items-center justify-between"
                >
                  <div>
                    <span v-if="item.priority" class="mr-2"
                      >Priority: {{ item.priority }}</span
                    >
                    <span v-if="item.dueDate"
                      >Due: {{ formatDate(item.dueDate) }}</span
                    >
                  </div>
                  <div class="flex gap-2">
                    <button
                      @click.stop="openAssign(item)"
                      class="px-2 py-1 rounded-lg border border-zinc-700 text-gray-300 hover:bg-zinc-800"
                    >
                      Assign
                    </button>
                    <button @click.stop="toggleComments(item.id)" class="px-2 py-1 rounded-lg border border-zinc-700 text-gray-300 hover:bg-zinc-800">Comments</button>
                  </div>
                </div>
                <div
                  v-if="assignOpenFor === item.id"
                  class="mt-2 border border-zinc-800 rounded-xl bg-black-secondary p-3"
                  @click.stop
                >
                  <div class="text-xs text-zinc-400 mb-2">
                    Assign team members
                  </div>
                  <div class="grid grid-cols-2 gap-2 max-h-40 overflow-auto">
                    <label
                      v-for="m in teamMembers"
                      :key="m.id"
                      class="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :checked="
                          assigneesSelection[item.id]?.has(m.email || '')
                        "
                        @change="toggleAssignee(item.id, m.email || '')"
                      />
                      <div
                        class="inline-flex items-center justify-center rounded-full border border-zinc-700 bg-black-primary text-[10px] h-6 w-7 text-gold-primary"
                      >
                        {{ initialsOf(m) }}
                      </div>
                      <div class="text-sm text-zinc-200">
                        {{ m.fullName || m.email }}
                      </div>
                    </label>
                  </div>
                  <div class="mt-3 flex justify-end gap-2">
                    <button
                      @click="cancelAssign"
                      class="px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      Cancel
                    </button>
                    <button
                      @click="saveAssignees(item)"
                      class="px-3 py-1.5 rounded-lg bg-gold-primary text-black border border-gold-secondary"
                    >
                      Save
                    </button>
                  </div>
                </div>
                <div
                  v-if="commentsOpenFor === item.id"
                  class="mt-3 border-t border-zinc-800 pt-3"
                  @click.stop
                >
                  <div v-if="commentsLoading" class="text-sm text-gray-400">
                    Loading comments...
                  </div>
                  <div v-else>
                    <div v-if="!comments.length" class="text-sm text-gray-500">
                      No comments yet.
                    </div>
                    <div v-else class="space-y-2">
                      <div v-for="c in comments" :key="c.id" class="text-sm">
                        <div class="text-gray-300">{{ c.message }}</div>
                        <div class="text-xs text-gray-500">
                          — {{ c.author || "unknown" }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mt-2 flex gap-2">
                    <input
                      v-model="newComment"
                      placeholder="Write a comment"
                      class="flex-1 px-3 py-2 rounded bg-black-primary border border-zinc-700"
                    />
                    <button
                      @click="submitComment(item.id)"
                      class="px-3 py-2 rounded bg-gold-primary text-black"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- KPIs Tab -->
      <div v-else-if="tab === 'kpi'" class="bg-black-secondary rounded-2xl p-2 border border-zinc-800">
        <KPIsDashboard />
      </div>

      <!-- Ticket Detail Modal -->
      <!-- New Task Modal -->
      <div v-if="showAddTaskModal" class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-lg backdrop-saturate-150" @click="closeNewTaskModal" />
        <div class="relative z-10 w-full max-w-2xl bg-black-secondary border border-zinc-800 rounded-2xl shadow-xl">
          <div class="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div class="text-2xl font-semibold text-gold-primary">New Task</div>
            <button @click="closeNewTaskModal" class="px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Close</button>
          </div>
          <div class="p-4 space-y-3">
            <input v-model="newTaskTitle" placeholder="Task — write a clear, actionable title" class="w-full px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500" />
            <textarea v-model="newTaskDescription" placeholder="Details — acceptance criteria, links" class="w-full px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500 min-h-[120px]" />
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select v-model="newTaskPriority" class="px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 text-zinc-200">
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
              <input v-model="newTaskDueDate" type="date" class="px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 text-zinc-200" />
              <input v-model="newTaskLabels" placeholder="Labels — performance, seo, ops" class="px-4 py-3 rounded-xl bg-black-primary border border-zinc-700 placeholder:text-zinc-500" />
            </div>
            <div v-if="newTaskError" class="text-sm text-red-400 border border-red-900/40 bg-red-900/10 rounded-lg px-3 py-2">{{ newTaskError }}</div>
            <div class="flex justify-end">
              <button :disabled="isCreatingTask" @click="submitNewTask" class="px-5 py-2.5 rounded-lg bg-gold-primary text-black font-semibold hover:bg-gold-secondary disabled:opacity-50 disabled:cursor-not-allowed">Create Task</button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showTicketModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
      >
        <div
          class="absolute inset-0 bg-black/30 backdrop-blur-lg backdrop-saturate-150"
          @click="closeTicket"
        />
        <div
          class="relative z-10 w-full max-w-3xl bg-black-secondary border border-zinc-800 rounded-2xl shadow-xl"
        >
          <div
            class="p-4 border-b border-zinc-800 flex items-center justify-between"
          >
            <div>
              <div class="text-xs uppercase tracking-wide text-zinc-400">
                Ticket
              </div>
              <div class="text-2xl font-semibold text-gold-primary">
                {{ activeTicket?.title }}
              </div>
            </div>
            <button
              @click="closeTicket"
              class="px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Close
            </button>
          </div>
          <div class="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="md:col-span-2 space-y-3">
              <div>
                <div class="text-xs text-zinc-400 mb-1">Details</div>
                <div
                  class="bg-black-primary/70 rounded-xl border border-zinc-700 p-4"
                >
                  <div class="text-sm text-zinc-200 whitespace-pre-line">
                    {{ activeTicket?.description || "No description." }}
                  </div>
                </div>
              </div>
              <div>
                <div class="text-xs text-zinc-400 mb-1">Comments</div>
                <div
                  class="bg-black-primary/70 rounded-xl border border-zinc-700 p-3"
                >
                  <div
                    v-if="activeTicketCommentsLoading"
                    class="text-sm text-gray-400"
                  >
                    Loading comments…
                  </div>
                  <div v-else>
                    <div
                      v-if="!activeTicketComments.length"
                      class="text-sm text-gray-500"
                    >
                      No comments yet.
                    </div>
                    <div v-else class="space-y-2">
                      <div
                        v-for="c in activeTicketComments"
                        :key="c.id"
                        class="text-sm"
                      >
                        <div class="text-gray-300">{{ c.message }}</div>
                        <div class="text-xs text-gray-500">
                          — {{ c.author || "unknown" }}
                        </div>
                      </div>
                    </div>
                    <div class="mt-2 flex gap-2">
                      <input
                        v-model="newTicketComment"
                        placeholder="Write a comment"
                        class="flex-1 px-3 py-2 rounded bg-black-primary border border-zinc-700"
                      />
                      <button
                        @click="submitTicketComment"
                        class="px-3 py-2 rounded bg-gold-primary text-black"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="space-y-3">
              <div
                class="bg-black-primary/70 rounded-xl border border-zinc-700 p-4"
              >
                <div class="text-xs text-zinc-400">Status</div>
                <div class="text-sm text-zinc-200">
                  {{ activeTicket?.status }}
                </div>
              </div>
              <div
                class="bg-black-primary/70 rounded-xl border border-zinc-700 p-4"
              >
                <div class="text-xs text-zinc-400">Priority</div>
                <div class="text-sm text-zinc-200">
                  {{ activeTicket?.priority || "—" }}
                </div>
              </div>
              <div
                class="bg-black-primary/70 rounded-xl border border-zinc-700 p-4"
              >
                <div class="text-xs text-zinc-400">Due Date</div>
                <div class="text-sm text-zinc-200">
                  {{
                    activeTicket?.dueDate
                      ? formatDate(activeTicket?.dueDate as any)
                      : "—"
                  }}
                </div>
              </div>
              <div
                class="bg-black-primary/70 rounded-xl border border-zinc-700 p-4"
              >
                <div class="text-xs text-zinc-400">Labels</div>
                <div class="mt-1 flex flex-wrap gap-1">
                  <span
                    v-for="lbl in activeTicket?.labels || []"
                    :key="lbl"
                    class="text-[10px] px-2 py-0.5 rounded-full border border-gold-primary text-gold-primary"
                    >{{ lbl }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-black-primary {
  background: #181818;
}
.bg-black-secondary {
  background: #0c0c0c;
}
</style>
